/* eslint react-hooks/exhaustive-deps: "warn" */

import NextLink from 'next/link';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';

import styles from './HubTopicPage.module.scss';

import { HubGroupRef, HubTopicLink } from './HubRoute.types';
import { HubGuide, HubTopic, HubTopicCard } from './content.types';
import { familyPath, groupPath, hubPath } from './paths';
import { IndexSection } from './atoms/IndexSection/IndexSection';
import { BreadcrumbItem } from './molecules/Breadcrumb/Breadcrumb.types';
import { TopicAside } from './molecules/TopicAside/TopicAside';
import { TopicCard } from './molecules/TopicCard/TopicCard';
import { TopicToc } from './molecules/TopicToc/TopicToc';
import { TopicHero } from './sections/TopicHero/TopicHero';
import { ShortAnswer } from './sections/ShortAnswer/ShortAnswer';
import { DefinitionSection } from './sections/DefinitionSection/DefinitionSection';
import { ProcessSection } from './sections/ProcessSection/ProcessSection';
import { ExampleSection } from './sections/ExampleSection/ExampleSection';
import { ComparisonSection } from './sections/ComparisonSection/ComparisonSection';
import { EvidenceReview } from './sections/EvidenceReview/EvidenceReview';
import { PractitionerNote } from './sections/PractitionerNote/PractitionerNote';
import { LimitsSection } from './sections/LimitsSection/LimitsSection';
import { SearchQuestions } from './sections/SearchQuestions/SearchQuestions';
import { FaqSection } from './sections/FaqSection/FaqSection';
import { ReadingPath } from './sections/ReadingPath/ReadingPath';
import { SourcesSection } from './sections/SourcesSection/SourcesSection';

interface HubTopicPageProps {
  topic: HubTopic;
  group: HubGroupRef;
  related: HubTopicCard[];
  readingPath: HubTopicLink[];
  guides: HubGuide[];
  lang?: string;
}

const CONTENT_ID = 'topic-content';
const RELATED_CARDS = 3;

/**
 * One template rendering any of the 300 topic pages from a topic record.
 * Every section is null-safe (returns null when its field is empty), so the
 * core 6-section topics and the 18 expanded ones share this exact list.
 */
export const HubTopicPage = ({ topic, group, related, readingPath, guides, lang }: HubTopicPageProps) => {
  const { t } = useInt();

  const family = { name: topic.subgroup_name, href: familyPath(group.slug, topic.subgroup, lang) };

  const breadcrumb: BreadcrumbItem[] = [
    { label: 'Knowledge', href: hubPath(lang) },
    { label: group.title, href: groupPath(group.slug, lang) },
    { label: family.name, href: family.href },
    { label: topic.name },
  ];

  return (
    <article>
      <TopicHero topic={topic} groupTitle={group.title} breadcrumb={breadcrumb} />

      <div className={styles['layout']}>
        <TopicToc contentId={CONTENT_ID} className={styles['toc']} />

        <div id={CONTENT_ID} className={styles['content']}>
          <ShortAnswer answer={topic.definition} />

          <DefinitionSection topic={topic} />

          <ProcessSection topic={topic} />

          <ExampleSection topic={topic} />

          <ComparisonSection topic={topic} />

          <EvidenceReview topic={topic} />

          <PractitionerNote topic={topic} />

          <LimitsSection topic={topic} />

          <SearchQuestions topic={topic} />

          <FaqSection topic={topic} />

          <ReadingPath readingPath={readingPath} family={family} lang={lang} />

          <SourcesSection guides={guides} />
        </div>

        <TopicAside related={related} product={topic.product} lang={lang} className={styles['aside']} />
      </div>

      {related.length > 0 && (
        <IndexSection
          title={t('Related topics')}
          aside={
            <NextLink href={hubPath(lang)} className={styles['allTopics']}>
              {t('All topics')}
            </NextLink>
          }
        >
          <ul className={styles['cards']}>
            {related.slice(0, RELATED_CARDS).map((card) => (
              <li key={card.slug} className={styles['cell']}>
                <TopicCard topic={card} lang={lang} />
              </li>
            ))}
          </ul>
        </IndexSection>
      )}

      <CtaSection
        spotlight
        title={t('See the registry record behind every answer.')}
        description={t(
          'Primary, government-sourced data on 600M+ companies in 200+ countries, with full source attribution.',
        )}
        actions={
          <>
            <Button type="blue" size="xl" href="/book-a-demo" suffixIcon="arrow-forward">
              {t('Book a demo')}
            </Button>

            <Button type="dark-outline" size="xl" href="/contact-us">
              {t('Talk to us')}
            </Button>
          </>
        }
      />
    </article>
  );
};
